import { FormEvent, useState } from 'react';
import { Check, ChevronDown, Loader2, Mail } from 'lucide-react';

type LeadCaptureProps = {
  tool: 'diagnostic-projet' | 'orientation-motorisation' | 'comparateur-cout-global';
  resultText: string;
  inputs: Record<string, unknown>;
};

export const LeadCapture = ({ tool, resultText, inputs }: LeadCaptureProps) => {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState({
    firstName: '',
    email: '',
    privacyAccepted: false,
    followupConsent: false,
  });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'delivery-error' | 'error'>('idle');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState('sending');
    const website = new FormData(event.currentTarget).get('company');
    const params = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
    let referrer = '';
    if (typeof document !== 'undefined' && document.referrer) {
      try {
        const url = new URL(document.referrer);
        referrer = `${url.origin}${url.pathname}`;
      } catch { /* Ignore malformed browser referrers. */ }
    }

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          tool,
          resultText,
          inputs,
          sourcePath: typeof window === 'undefined' ? `/outils/${tool}/` : window.location.pathname,
          referrer,
          utm: {
            source: params.get('utm_source') || '',
            medium: params.get('utm_medium') || '',
            campaign: params.get('utm_campaign') || '',
          },
          website,
        }),
      });
      if (!response.ok) throw new Error('lead unavailable');
      const result = await response.json();
      setState(result.resultDelivered ? 'sent' : 'delivery-error');
    } catch {
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <div className="as-lead-success" role="status">
        <Check size={20} aria-hidden="true" />
        <div>
          <strong>Votre copie est en route.</strong>
          <p>
            {data.followupConsent
              ? 'Vous recevrez aussi deux conseils, à J+3 et J+7. Vous pourrez vous désinscrire en un clic.'
              : 'Aucune relance marketing ne sera envoyée.'}
          </p>
        </div>
      </div>
    );
  }

  if (state === 'delivery-error') {
    return (
      <div className="as-lead-success as-lead-delivery-error" role="status">
        <Mail size={20} aria-hidden="true" />
        <div><strong>Votre demande est enregistrée, mais la copie n’a pas pu partir.</strong><p>Le résultat reste visible sur cette page. Enzo pourra retrouver votre demande dans le back-office.</p></div>
      </div>
    );
  }

  const formId = `lead-copy-${tool}`;

  return (
    <section className={`as-lead-capture${expanded ? ' is-open' : ''}`}>
      <button
        className="as-lead-toggle"
        type="button"
        aria-expanded={expanded}
        aria-controls={formId}
        onClick={() => setExpanded((value) => !value)}
      >
        <Mail size={19} aria-hidden="true" />
        <div>
          <strong>Garder une copie de ce résultat</strong>
          <p>Facultatif · votre résultat reste visible sans e-mail.</p>
        </div>
        <ChevronDown size={18} aria-hidden="true" />
      </button>
      {expanded && (
        <form id={formId} className="as-lead-form" onSubmit={submit}>
          <label className="as-honeypot" aria-hidden="true">
            Votre société<input name="company" type="text" tabIndex={-1} autoComplete="off" />
          </label>
          <div className="as-lead-fields">
            <label>Prénom<input required maxLength={80} autoComplete="given-name" value={data.firstName} onChange={(event) => setData({ ...data, firstName: event.target.value })} /></label>
            <label>E-mail<input required type="email" maxLength={200} autoComplete="email" value={data.email} onChange={(event) => setData({ ...data, email: event.target.value })} /></label>
          </div>
          <label className="as-form-consent">
            <input type="checkbox" required checked={data.privacyAccepted} onChange={(event) => setData({ ...data, privacyAccepted: event.target.checked })} />
            <span>J’accepte l’utilisation de ces informations pour m’envoyer le résultat demandé. <a href="/confidentialite/">En savoir plus</a>.</span>
          </label>
          <label className="as-form-consent as-followup-consent">
            <input type="checkbox" checked={data.followupConsent} onChange={(event) => setData({ ...data, followupConsent: event.target.checked })} />
            <span>Je souhaite recevoir exactement deux conseils complémentaires par e-mail, à J+3 et J+7. Cette case est facultative et décochée par défaut.</span>
          </label>
          {state === 'error' && <p className="as-form-error" role="alert">L’envoi est momentanément indisponible. Votre résultat reste visible ci-dessus.</p>}
          <button className="as-button as-button-dark" type="submit" disabled={state === 'sending'}>
            {state === 'sending' ? <><Loader2 className="as-spinner" size={17} /> Envoi…</> : 'M’envoyer le résultat'}
          </button>
        </form>
      )}
    </section>
  );
};
